import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Select, InputNumber } from "antd";
import { Button, CircularProgress, MenuItem } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";

import H4 from "../../../../../GlobalComponents/TypoGraphy/H4";
import H5 from "../../../../../GlobalComponents/TypoGraphy/H5";
import { Colors } from "../../../../../GlobalComponents/Utility/Colors";
import GridItem from "../../../../../GlobalComponents/Grid/GridItem";
import GridContainer from "../../../../../GlobalComponents/Grid/GridContainer";
import Snackbars from "../../../../../GlobalComponents/Alerts/Snackbars";

import {
  fetchTicketById,
  updateTicket,
} from "../../../../../ReduxTLK/features/SAV/TicketSlice";
import {
  array_machine,
  array_typeticket,
  array_impact,
  array_priorite,
} from "../../../../../ReduxTLK/features/SAV/ticketList";
import { fetchUsersAgentSAV } from "../../../../../ReduxTLK/features/User/UserSlice";
import { fetchChaines } from "../../../../../ReduxTLK/features/Chaines/ChaineSlice";
import { fetchPventes } from "../../../../../ReduxTLK/features/Pointdeventes/PointdeventeSlice";
import {
  resetEditTicketDraftByIndex,
  setEditTicketDraft,
} from "../../../../../ReduxTLK/features/SAV/NewTicketDraftSlice";

import AddNotesInternes from "../../../../../Pages/SAV/NotesInternes/AddNotesInternes";
import NotesList from "../../../../../Pages/SAV/NotesInternes/NotesList";
import { Persistor } from "../../../../..";
import { CANAL_OPTIONS, DELAI_RESOLUTION_OPTIONS } from "../../../../../constants";
import { translateTimeLabel } from "../../../../../utils/labelTranslator";
import { ticketService } from "../../../../../service/ticketService";
import { getSubCategories } from "../../../../../utils/savUtils";

import styles from "./TicketDetailsTab.module.css";

const layout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 22 },
};

/* ---------- Icons ---------- */

const EyeIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const LockIcon = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/* ---------- Component ---------- */

export default function TicketDetailsTab({
  ticket,
  setTicket,
  onCheckRequiredFields,
  formRef,
  /** When true, all fields are disabled, the save button is hidden,
   *  and AddNotesInternes is not rendered. */
  viewOnly = false,
  ...props
}) {
  const [loading, setLoading] = useState(false);
  const [openErrorBox, setOpenErrorBox] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [onePV, setOnePV] = useState(false);
  const [SAVCategories, setSAVCategories] = useState([]);

  const { TextArea } = Input;
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  const [severity, setSeverity] = useState("success");
  const [message, setMessage] = useState("");

  const symbol = "#";
  const [pageTitle, setPageTitle] = useState();
  const [etablissement, setEtablissement] = useState();
  const [pointDeVente, setPointDeVente] = useState();

  const editedTicketDraft = useSelector(
    (state) => state.draftNewTicket.editedTicketDraft
  );
  const agentsSAV = useSelector((state) => state.user.agentsSAV);
  const chaines = useSelector((state) => state.chaine.chaines);
  const pointDeVentes = useSelector((state) => state.pointDeVente.pventes);

  const [pVName, setPVName] = useState("");

  /* ---------- Effects ---------- */

  useEffect(() => {
    dispatch(fetchUsersAgentSAV());
    dispatch(fetchChaines());
  }, []);

  useEffect(() => {
    const dirtyEditIndex = editedTicketDraft.findIndex(
      (item) => item.id === ticket?.id
    );

    /* In view-only mode, always load fresh data — ignore drafts */
    if (!viewOnly && dirtyEditIndex !== -1) {
      setDirty(true);
      form.setFieldsValue(editedTicketDraft[dirtyEditIndex]?.updatedItem);
    } else {
      setLoading(true);
      dispatch(fetchTicketById(ticket?.id))
        .then(unwrapResult)
        .then((res) => {
          const getintervenant =
            (res?.intervenantFirstname || "") +
            " " +
            (res?.intervenantLastname || "");
          const getAuteur =
            (res?.iUser?.nom || "") + " " + (res?.iUser?.prenom || "");

          form.setFieldsValue({
            etablissement: res?.idChaine,
            pointDeVente: res?.idPv,
            machine: res?.machine,
            type: res?.type,
            sujet: res?.sujet,
            priorite: res?.priorite,
            intervenant: getintervenant,
            auteur: getAuteur,
            impact: res?.impact,
            idAssignee: res?.idAssigne,
            telephone: res?.intervenantPhone,
            description: res?.description,
            canal: Number(res?.canal),
            delaiResolution: res?.delaiResolution
              ? Number(res?.delaiResolution)
              : null,
            category: res?.category,
            subCategory: res?.subCategory,
          });
        })
        .then(() => setLoading(false));
    }
  }, [ticket, viewOnly]);

  useEffect(() => {
    ticketService
      .getSAVCategories()
      .then((res) => setSAVCategories(res))
      .catch((error) =>
        console.error("Error fetching SAV categories:", error)
      );
  }, []);

  useEffect(() => {
    if (etablissement == !undefined || etablissement == !null) {
      dispatch(fetchPventes(etablissement));
    }
  }, [etablissement]);

  /* ---------- Data ---------- */

  const fetchData = async () => {
    dispatch(fetchTicketById(ticket?.id))
      .then(unwrapResult)
      .then((res) => {
        setTicket(res);
        setPageTitle(`${symbol}${res?.id}`);
      });
  };

  /* ---------- Submit ---------- */

  const onFinishUpdate = async (values) => {
    if (viewOnly || !dirty) return;

    onCheckRequiredFields(form);

    const { delaiResolution, ...rest } = values;

    try {
      const response = await dispatch(
        updateTicket({
          id: ticket?.id,
          modified: {
            ...rest,
            délaiRésolution: delaiResolution,
          },
        })
      );

      const result = response?.payload?.data?.data?.result;
      if (!result) throw new Error("No result in response");

      dispatch(resetEditTicketDraftByIndex(result.id));
      setDirty(false);
      setTicket(result);
      props.setDirty(false);
      Persistor.persist();

      updateFormFields(result, form);

      if (response?.payload?.data?.status?.toLowerCase() === "success") {
        setSeverity("success");
        setMessage("Les détails du ticket ont été mis à jour");
        setOpen(true);
      }
    } catch (error) {
      console.error("Ticket update error:", error);
      setSeverity("error");
      setMessage("Un problème est survenu lors de la mise à jours du ticket!");
      setOpenErrorBox(true);
    }
  };

  const getIntervenantName = (result) =>
    `${result.intervenantFirstname} ${result.intervenantLastname}`.trim();

  const getAuthorName = (result) =>
    `${result.iUser?.nom} ${result.iUser?.prenom}`.trim();

  const updateFormFields = (result, form) => {
    form.setFieldsValue({
      etablissement: result.idChaine,
      pointDeVente: result.idPv,
      machine: result.machine,
      type: result.type,
      sujet: result.sujet,
      priorite: result.priorite,
      intervenant: getIntervenantName(result),
      auteur: getAuthorName(result),
      impact: result.impact,
      idAssignee: result.idAssigne,
      telephone: result.intervenantPhone,
      description: result.description,
      canal: Number(result.canal),
      delaiResolution: result.delaiResolution,
      category: result.category,
      subCategory: result.subCategory,
    });
  };

  /* ---------- Handlers ---------- */

  const onChangePV = (item) => {
    if (viewOnly) return;
    setPointDeVente(item);
  };

  const onChangeEtablissement = (item) => {
    if (viewOnly) return;
    form.setFieldsValue({
      pointDeVente: null,
      intervenantId: null,
    });
    setOnePV(false);
    setEtablissement(item);
    dispatch(fetchPventes(item))
      .then(unwrapResult)
      .then((res) => {
        if (res.data.data.result.length === 1) {
          setPointDeVente(res.data.data.result[0].id);
          setPVName(res.data.data.result[0].nom);
          setOnePV(true);
          form.setFieldsValue({
            pointDeVente: res.data.data.result[0].id,
          });
        }
      });
  };

  const validateNonEmptyContent = (rule, value, callback) => {
    if (!value || value.trim() === "") {
      callback("Veuillez fournir un texte pertinent.");
    } else {
      callback();
    }
  };

  const handleClearPrioriteSelection = () => {
    if (viewOnly) return;
    form.setFieldsValue({ priorite: null });
  };

  const onValuesChange = (changedValues, allValues) => {
    /* No draft persistence in view mode */
    if (viewOnly) return;

    props.setDirty?.(true);
    if (changedValues) {
      setDirty(true);
      let editedTicketDraftCopy = [...editedTicketDraft];
      const existingIndex = editedTicketDraftCopy.findIndex(
        (t) => t.id === ticket?.id
      );
      if (existingIndex !== -1) {
        editedTicketDraftCopy = editedTicketDraftCopy.map((item) => {
          if (item.id === ticket?.id) {
            const updatedItem = { ...item.updatedItem, ...changedValues };
            return { ...item, updatedItem };
          }
          return item;
        });
      } else {
        editedTicketDraftCopy = [
          ...editedTicketDraftCopy,
          {
            updatedItem: allValues,
            id: ticket?.id,
          },
        ];
      }
      dispatch(setEditTicketDraft(editedTicketDraftCopy));
      Persistor.persist();
    }
  };

  const filterOption = (input, option) =>
    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;

  /* ---------- Render ---------- */

  /** When in view mode, force disabled on every field via a single flag */
  const isDisabled = viewOnly;

  return (
    <div className={styles.mainContainer}>
      <div className={styles.formWrapper}>
        {loading ? (
          <div className={styles.loaderWrapper}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            <div
              className={`${styles.formContent} ${
                viewOnly ? styles.readOnlyMode : ""
              }`}
            >
              <Form
                {...layout}
                form={form}
                name="control-hooks"
                onFinish={onFinishUpdate}
                onValuesChange={onValuesChange}
                ref={formRef}
              >
                <Form.Item>
                  <div className={styles.headerRow}>
                    <div className={styles.headerItem}>
                      <H4
                        title={
                          viewOnly
                            ? "Détails du ticket (lecture seule)"
                            : "Détails du ticket :"
                        }
                        color={Colors.primary}
                      />
                      {viewOnly && (
                        <span className={styles.viewBadge}>
                          <span className={styles.viewBadgeIcon}>
                            <LockIcon size={11} />
                          </span>
                          Vue
                        </span>
                      )}
                    </div>
                    {!viewOnly && (
                      <div className={styles.buttonContainer}>
                        <AddNotesInternes
                          idTicket={ticket?.id}
                          fetchData={fetchData}
                        />
                        <Button
                          className={styles.saveButton}
                          type="submit"
                          color="primary"
                          variant="contained"
                        >
                          Sauvegarder
                        </Button>
                      </div>
                    )}
                  </div>
                </Form.Item>

                {viewOnly && (
                  <div className={styles.readOnlyBanner}>
                    <span className={styles.readOnlyBannerIcon}>
                      <EyeIcon size={14} />
                    </span>
                    Ce ticket est affiché en mode lecture seule. Aucune
                    modification n'est possible.
                  </div>
                )}

                <GridContainer>
                  <GridItem sx={12} sm={6} md={4} xl={4} xxl={4}>
                    <H5 title={"Enterprise"} requiredInput={!viewOnly} />
                    <Form.Item
                      name="etablissement"
                      rules={
                        viewOnly
                          ? []
                          : [
                              {
                                required: true,
                                message: "Ce champ est obligatoire !",
                              },
                            ]
                      }
                    >
                      <Select
                        showSearch
                        filterOption={filterOption}
                        disabled
                        placeholder=""
                        className={styles.selectDefault}
                        onChange={onChangeEtablissement}
                      >
                        {chaines &&
                          chaines.map((chaine) => (
                            <option key={chaine.id} value={chaine.id}>
                              {chaine.nom}
                            </option>
                          ))}
                      </Select>
                    </Form.Item>

                    <H5 title={"Canal"} requiredInput={false} />
                    <Form.Item name="canal">
                      <Select
                        showSearch
                        filterOption={filterOption}
                        placeholder=""
                        disabled={isDisabled}
                      >
                        {CANAL_OPTIONS &&
                          CANAL_OPTIONS.map((canal) => (
                            <option key={canal.value} value={canal.value}>
                              {canal.label}
                            </option>
                          ))}
                      </Select>
                    </Form.Item>

                    <H5 title={"Machine"} />
                    <Form.Item name="machine">
                      <Select
                        showSearch
                        filterOption={filterOption}
                        placeholder=""
                        disabled={isDisabled}
                      >
                        {array_machine &&
                          array_machine.map((machine) => (
                            <option key={machine.id} value={machine.id}>
                              {machine.nom}
                            </option>
                          ))}
                      </Select>
                    </Form.Item>

                    <H5 title={"Catégorie"} requiredInput={!viewOnly} />
                    <Form.Item
                      name="category"
                      rules={
                        viewOnly
                          ? []
                          : [
                              {
                                required: true,
                                message: "Ce champ est obligatoire !",
                              },
                            ]
                      }
                    >
                      <Select
                        showSearch
                        filterOption={filterOption}
                        placeholder=""
                        disabled={isDisabled}
                        onChange={() => {
                          if (!viewOnly) {
                            form.setFieldsValue({ subCategory: undefined });
                          }
                        }}
                      >
                        {SAVCategories &&
                          Object.keys(SAVCategories).map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                      </Select>
                    </Form.Item>

                    <H5 title={"Sujet"} requiredInput={!viewOnly} />
                    <Form.Item
                      name="sujet"
                      rules={
                        viewOnly
                          ? []
                          : [
                              {
                                maxLength: 100,
                                required: true,
                                message: "Ce champ est obligatoire !",
                                validator: validateNonEmptyContent,
                              },
                            ]
                      }
                    >
                      <Input
                        maxLength="100"
                        count={{ show: !viewOnly, max: 100 }}
                        disabled={isDisabled}
                      />
                    </Form.Item>

                    <H5 title={"Priorité"} requiredInput={!viewOnly} />
                    <Form.Item
                      name="priorite"
                      rules={
                        viewOnly
                          ? []
                          : [
                              {
                                required: true,
                                message: "Ce champ est obligatoire !",
                              },
                            ]
                      }
                    >
                      <Select
                        allowClear={!viewOnly}
                        showSearch
                        filterOption={filterOption}
                        onClear={handleClearPrioriteSelection}
                        placeholder=""
                        disabled={isDisabled}
                      >
                        {array_priorite &&
                          array_priorite.map((priorite) => (
                            <option key={priorite.id} value={priorite.id}>
                              {priorite.nom}
                            </option>
                          ))}
                      </Select>
                    </Form.Item>

                    <H5 title={"Intervenant"} requiredInput={false} />
                    <Form.Item name="intervenant">
                      <Input
                        className={styles.inputDisabled}
                        disabled
                        placeholder=""
                      />
                    </Form.Item>

                    <H5
                      title={"Auteur"}
                      requiredInput={false}
                      fontWeight="500"
                    />
                    <Form.Item name="auteur">
                      <Input
                        className={styles.inputDisabled}
                        disabled
                        placeholder=""
                      />
                    </Form.Item>
                  </GridItem>

                  <GridItem sx={12} sm={6} md={4} xl={4} xxl={4}>
                    <H5 title={"Établissement"} requiredInput={!viewOnly} />
                    {onePV ? (
                      <Form.Item
                        name="pointDeVente"
                        initialValue={pointDeVente}
                        rules={
                          viewOnly
                            ? []
                            : [
                                {
                                  required: true,
                                  message: "Ce champ est obligatoire !",
                                },
                              ]
                        }
                      >
                        <Select
                          className={styles.selectHighlight}
                          showSearch
                          filterOption={filterOption}
                          placeholder=""
                          onChange={onChangePV}
                          disabled={isDisabled}
                        >
                          {pointDeVentes &&
                            pointDeVentes.map((pv) => (
                              <option key={pv.id} value={pv.id}>
                                {pv.nom}
                              </option>
                            ))}
                        </Select>
                      </Form.Item>
                    ) : (
                      <Form.Item
                        name="pointDeVente"
                        rules={
                          viewOnly
                            ? []
                            : [
                                {
                                  required: true,
                                  message: "Ce champ est obligatoire !",
                                },
                              ]
                        }
                      >
                        <Select
                          showSearch
                          filterOption={filterOption}
                          placeholder=""
                          onChange={onChangePV}
                          disabled={isDisabled}
                        >
                          {pointDeVentes &&
                            pointDeVentes.map((pv) => (
                              <option key={pv.id} value={pv.id}>
                                {pv.nom}
                              </option>
                            ))}
                        </Select>
                      </Form.Item>
                    )}

                    <H5
                      title={"Estimation de la résolution"}
                      requiredInput={false}
                    />
                    <Form.Item name="delaiResolution">
                      <Select
                        showSearch
                        filterOption={filterOption}
                        placeholder=""
                        disabled={isDisabled}
                      >
                        {DELAI_RESOLUTION_OPTIONS &&
                          DELAI_RESOLUTION_OPTIONS.map((delai) => (
                            <option key={delai.value} value={delai.value}>
                              {translateTimeLabel(delai.label)}
                            </option>
                          ))}
                      </Select>
                    </Form.Item>

                    <H5 title={"Type de problème"} requiredInput={!viewOnly} />
                    <Form.Item name="type">
                      <Select
                        showSearch
                        filterOption={filterOption}
                        placeholder=""
                        disabled={isDisabled}
                      >
                        {array_typeticket &&
                          array_typeticket.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.nom}
                            </option>
                          ))}
                      </Select>
                    </Form.Item>

                    <H5 title={"Sous-Catégorie"} requiredInput={!viewOnly} />
                    <Form.Item
                      name="subCategory"
                      rules={
                        viewOnly
                          ? []
                          : [
                              {
                                required: true,
                                message: "Ce champ est obligatoire !",
                              },
                            ]
                      }
                    >
                      <Select
                        showSearch
                        filterOption={filterOption}
                        placeholder=""
                        disabled={isDisabled}
                      >
                        {SAVCategories &&
                          getSubCategories(
                            SAVCategories,
                            form.getFieldValue("category")
                          ).map((subCategory) => (
                            <option key={subCategory} value={subCategory}>
                              {subCategory}
                            </option>
                          ))}
                      </Select>
                    </Form.Item>

                    <H5 title={"Impact de ticket"} requiredInput={false} />
                    <Form.Item name="impact">
                      <Select
                        showSearch
                        filterOption={filterOption}
                        placeholder=""
                        disabled={isDisabled}
                      >
                        {array_impact &&
                          array_impact.map((impact) => (
                            <option key={impact.id} value={impact.id}>
                              {impact.nom}
                            </option>
                          ))}
                      </Select>
                    </Form.Item>

                    <H5 title={"Assigner à"} requiredInput={!viewOnly} />
                    <Form.Item
                      name="idAssignee"
                      rules={
                        // viewOnly
                          // ? 
                          []
                          // : [
                          //     {
                          //       required: true,
                          //       message: "Ce champ est obligatoire !",
                          //     },
                          //   ]
                      }
                    >
                      <Select
                        showSearch
                        filterOption={filterOption}
                        placeholder=""
                        disabled={true}
                      >
                        {agentsSAV.map((option, index) => (
                          <MenuItem key={index} value={option.id}>
                            {`${option.nom} ${option.prenom}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </Form.Item>

                    <H5 title={"Téléphone"} requiredInput={false} />
                    <Form.Item name="telephone">
                      <InputNumber
                        disabled
                        className={styles.inputNumberDisabled}
                      />
                    </Form.Item>
                  </GridItem>

                  <GridItem
                    style={{ width: "100%" }}
                    sx={12}
                    sm={6}
                    md={4}
                    xl={4}
                    xxl={4}
                  >
                    <H5 title={"Description"} requiredInput={!viewOnly} />
                    <Form.Item
                      name="description"
                      rules={
                        viewOnly
                          ? []
                          : [
                              {
                                required: true,
                                message: "Ce champ est obligatoire !",
                                maxLength: 350,
                                validator: validateNonEmptyContent,
                              },
                            ]
                      }
                    >
                      <TextArea
                        autoSize={{ minRows: 14, maxRows: 14 }}
                        rows={14}
                        maxLength="350"
                        count={{ show: !viewOnly, max: 350 }}
                        disabled={isDisabled}
                      />
                    </Form.Item>
                  </GridItem>
                </GridContainer>

                <Snackbars
                  severity="success"
                  message={message}
                  open={open}
                  setOpen={setOpen}
                  autoHideDuration={1000}
                />
                <Snackbars
                  severity="error"
                  message={message}
                  open={openErrorBox}
                  setOpen={setOpenErrorBox}
                  autoHideDuration={1000}
                />
              </Form>
            </div>

            <NotesList notes={ticket?.notesInternes} />
          </>
        )}
      </div>
    </div>
  );
}